/**
 * PipelineStageAccordion — vertical alternative to the horizontal kanban.
 *
 * Renders one collapsible row per stage from LEAD_STATUSES (single source
 * of truth). Each row shows: status color dot, label, lead count, optional
 * "attention" badge (count of leads with any signal in that stage), and a
 * caret. Expanding a row reveals the leads inside, rendered through the
 * same <LeadCard> used by the kanban so quick actions and the side panel
 * keep working unchanged.
 *
 * Stages with leads are expanded by default; empty stages are collapsed.
 */
import React, { useState, useMemo } from "react";
import { CaretDown, CaretRight, Warning } from "@phosphor-icons/react";
import { hasAnySignal } from "@/lib/leadStatuses";
import { VISIBLE_PIPELINE_WITH_COLD, visibleStep } from "@/lib/leadPipeline";
import LeadCard from "@/components/leads/LeadCard";

export default function PipelineStageAccordion({
  leads,
  assignees,
  onOpenLead,
  onPatch,
  selectable = false,
  selectedIds,
  onToggleSelect,
}) {
  // Group visible leads by the visible pipeline (9 + cold). Legacy statuses
  // such as dossier_pending / dossier / reservation_sent are rebucketed into
  // their adjacent canonical step via visibleStep().
  const byStage = useMemo(() => {
    const acc = Object.fromEntries(VISIBLE_PIPELINE_WITH_COLD.map((s) => [s.key, []]));
    for (const l of leads) {
      const k = visibleStep(l.status);
      if (acc[k]) acc[k].push(l);
    }
    return acc;
  }, [leads]);

  // Default expanded set = stages that have at least one lead.
  const initialOpen = useMemo(
    () => new Set(VISIBLE_PIPELINE_WITH_COLD.filter((s) => (byStage[s.key] || []).length > 0).map((s) => s.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [openKeys, setOpenKeys] = useState(initialOpen);

  const toggle = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const expandAll = () => setOpenKeys(new Set(VISIBLE_PIPELINE_WITH_COLD.map((s) => s.key)));
  const collapseAll = () => setOpenKeys(new Set());

  return (
    <div data-testid="pipeline-accordion">
      <div className="flex items-center justify-end gap-2 mb-3 text-xs">
        <button
          type="button"
          onClick={expandAll}
          data-testid="pipeline-expand-all"
          className="text-stone-600 hover:text-stone-900 px-2 py-1 rounded border border-stone-200 hover:border-stone-400 transition"
        >
          Expandir todo
        </button>
        <button
          type="button"
          onClick={collapseAll}
          data-testid="pipeline-collapse-all"
          className="text-stone-600 hover:text-stone-900 px-2 py-1 rounded border border-stone-200 hover:border-stone-400 transition"
        >
          Colapsar todo
        </button>
      </div>

      <div className="space-y-2">
        {VISIBLE_PIPELINE_WITH_COLD.map((st) => {
          const items = byStage[st.key] || [];
          const isOpen = openKeys.has(st.key);
          const attentionCount = items.filter(hasAnySignal).length;
          return (
            <section
              key={st.key}
              className="bg-white border border-stone-200 rounded-lg overflow-hidden"
              data-testid={`pipeline-stage-${st.key}`}
            >
              <button
                type="button"
                onClick={() => toggle(st.key)}
                aria-expanded={isOpen}
                aria-controls={`pipeline-body-${st.key}`}
                data-testid={`pipeline-stage-toggle-${st.key}`}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-50 transition-colors"
              >
                <span className="text-stone-400 shrink-0">
                  {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                </span>
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: st.dotColor }}
                  aria-hidden="true"
                />
                <span className="text-sm font-medium text-stone-800 flex-1 truncate">
                  {st.label}
                </span>
                {attentionCount > 0 && (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                    title="Leads con señales que requieren atención"
                    data-testid={`pipeline-stage-attention-${st.key}`}
                  >
                    <Warning size={11} weight="bold" /> {attentionCount}
                  </span>
                )}
                <span
                  className="inline-flex items-center justify-center min-w-[24px] h-[22px] text-xs px-2 rounded-full bg-stone-100 text-stone-700 border border-stone-200"
                  data-testid={`pipeline-stage-count-${st.key}`}
                >
                  {items.length}
                </span>
              </button>
              {isOpen && (
                <div
                  id={`pipeline-body-${st.key}`}
                  className="px-4 pb-4 pt-1 border-t border-stone-100"
                >
                  {items.length === 0 ? (
                    <div className="text-xs text-stone-400 italic py-2">
                      Sin leads en esta etapa.
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {items.map((l) => (
                        <LeadCard
                          key={l.lead_id}
                          lead={l}
                          assignees={assignees}
                          onOpen={() => onOpenLead && onOpenLead(l.lead_id)}
                          onPatch={onPatch}
                          selectable={selectable}
                          selected={selectedIds ? selectedIds.has(l.lead_id) : false}
                          onToggleSelect={onToggleSelect}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
